/**
 * @file: useHandleConstructionWorkItemPress.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { LinkButton } from '@euler/app/components/LinkButton';
import { useMemberPickerBottomSheetContext } from '@euler/app/components/MemberPickerBottomSheet';
import { WiredRememberLastAssignButton } from '@euler/app/components/settings/RememberLastAssignmentButton';
import { useUserLocalSettings } from '@euler/app/flows/settings/functions/useUserLocalSettings';
import {
  useConstructionJobScheduleContext,
  useScheduleConstructionJobs,
} from '@euler/app/flows/task/pages/construction/ConstructionJobScheduleContext';
import { ConstructionWorkItemInfo } from '@euler/app/flows/task/pages/construction/functions/useConstructionWorkItems';
import { HStack } from '@euler/components/layout/Stack';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { OrgMember } from '@euler/model/entity';
import { OrgUserRoleType } from '@euler/model/enum';
import { useBehaviorSubject, usePersistFn } from '@euler/utils/hooks';
import { Suspense, useRef } from 'react';
import { Keyboard } from 'react-native';

export function useHandleConstructionWorkItemPress() {
  const { rpx } = useResponsiveContext();

  const { selectedWorkItems, addWorkItem, removeWorkItem } =
    useScheduleConstructionJobs();

  const { skipJobAssignment$ } = useConstructionJobScheduleContext();

  const { memberPickerPresentationRequest$ } =
    useMemberPickerBottomSheetContext();

  const [skipJobAssignment, setSkipJobAssignment] =
    useBehaviorSubject(skipJobAssignment$);

  const lastAssignedMember = useRef<OrgMember>();
  const [{ rememberLastServiceSubjectAssignment }] = useUserLocalSettings();

  return usePersistFn((item: ConstructionWorkItemInfo) => {
    Keyboard.dismiss();

    const index = selectedWorkItems.findIndex(
      x => x.workItem.name === item.name,
    );

    if (index >= 0) {
      removeWorkItem(item);
      return;
    }

    if (
      skipJobAssignment ||
      (rememberLastServiceSubjectAssignment && lastAssignedMember.current)
    ) {
      addWorkItem(
        item,
        lastAssignedMember.current
          ? {
              id: lastAssignedMember.current.id,
              name: lastAssignedMember.current.name,
            }
          : undefined,
      );
      return;
    }

    memberPickerPresentationRequest$.next({
      title: '施工项目派工',
      subTitle: (
        <HStack justifyContent="center">
          <Suspense fallback={null}>
            <WiredRememberLastAssignButton />
          </Suspense>
          <LinkButton
            onPress={() => {
              setSkipJobAssignment(true);
              memberPickerPresentationRequest$.next(undefined);
              addWorkItem(item);
            }}
            text="本次不再弹出"
            textStyle={{
              fontSize: rpx(12),
              color: '#777',
              marginLeft: rpx(15),
            }}
          />
        </HStack>
      ),
      role: OrgUserRoleType.Technicians,
      onSelect: member => {
        memberPickerPresentationRequest$.next(undefined);
        lastAssignedMember.current = member;
        addWorkItem(item, { id: member.id, name: member.name });
      },
      onDismiss() {
        addWorkItem(item);
      },
    });
  });
}
